import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PlayerSessionAvatars } from "@/components/BgtAvatar/PlayerSessionAvatars";
import { BgtEditDeleteButtons } from "@/components/BgtButton/BgtEditDeleteButtons";
import { BgtCard } from "@/components/BgtCard/BgtCard";
import { BgtPage } from "@/components/BgtLayout/BgtPage";
import { BgtPageContent } from "@/components/BgtLayout/BgtPageContent";
import BgtPageHeader from "@/components/BgtLayout/BgtPageHeader";
import { BgtDataTable, type DataTableProps } from "@/components/BgtTable/BgtDataTable";
import { usePermissions } from "@/hooks/usePermissions";
import type { Session } from "@/models";
import { getGame, getGameSessions } from "@/services/queries/games";
import { getPlayers } from "@/services/queries/players";
import { getSettings } from "@/services/queries/settings";
import { gameIdParamSchema } from "@/utils/routeSchemas";
import { BgtDeleteModal } from "../-modals/BgtDeleteModal";
import { useGameSessionsData } from "./-hooks/useGameSessionsData";
import { SessionFactionsRow } from "./-components/SessionFactionsRow";

export const Route = createFileRoute("/games/$gameId_/sessions")({
	component: RouteComponent,
	params: gameIdParamSchema,
	loader: ({ params, context: { queryClient } }) => {
		queryClient.prefetchQuery(getGame(params.gameId));
		queryClient.prefetchQuery(getSettings());
		queryClient.prefetchQuery(getPlayers());
		queryClient.prefetchQuery(getGameSessions(params.gameId));
	},
});

function RouteComponent() {
	const { gameId } = Route.useParams();
	const { t } = useTranslation(["common", "sessions"]);
	const navigate = useNavigate();
	const { canWrite } = usePermissions();
	const [sessionToDelete, setSessionToDelete] = useState<number | null>(null);

	const { settings, game, sessions, deleteSession, players, isLoading } = useGameSessionsData({
		gameId,
		onDeleteSuccess: () => {
			setSessionToDelete(null);
		},
	});

	return (
		<BgtPage>
			<BgtPageContent isLoading={isLoading} data={{ game, settings, players }}>
				{({ game, settings, players }) => {
					const columns: DataTableProps<Session>["columns"] = [
						{
							accessorKey: "0",
							cell: ({ row }) => format(new Date(row.original.start), settings.dateFormat),
							header: t("date"),
						},
						{
							accessorKey: "1",
							cell: ({ row }) => format(new Date(row.original.start), settings.timeFormat),
							header: t("time"),
						},
						{
							accessorKey: "2",
							cell: ({ row }) => `${row.original.minutes} ${t("minutes", { count: row.original.minutes })}`,
							header: t("duration"),
						},
						{
							accessorKey: "3",
							cell: ({ row }) => (
								<PlayerSessionAvatars
									playerSessions={row.original.playerSessions}
									players={players}
									game={game}
									won={true}
								/>
							),
							header: t("winners"),
						},
						{
							accessorKey: "4",
							cell: ({ row }) => (
								<PlayerSessionAvatars
									playerSessions={row.original.playerSessions}
									players={players}
									game={game}
									won={false}
								/>
							),
							header: t("other-players"),
						},
						{
							accessorKey: "5",
							cell: ({ row }) => {
								const highScore = row.original.playerSessions
									.filter((x) => x.score !== undefined)
									.sort((a, b) => b.score! - a.score!);

								if (highScore.length === 0) return "";

								return highScore[0].score;
							},
							header: t("high-score"),
						},
						...(canWrite
							? [
									{
										accessorKey: "200",
										cell: ({ row }: { row: { original: Session } }) => (
											<BgtEditDeleteButtons
												onDelete={() => setSessionToDelete(row.original.id)}
												onEdit={() => navigate({ to: `/sessions/update/${row.original.id}` })}
											/>
										),
										header: () => <div className="flex justify-end">{t("actions")}</div>,
									},
								]
							: []),
					];

					return (
						<>
							<BgtPageHeader
								backAction={() => navigate({ to: `/games/${gameId}` })}
								header={`${game.title} - ${t("sessions:title")}`}
								actions={
									canWrite
										? [
												{
													onClick: () => navigate({ to: `/sessions/new/${gameId}` }),
													variant: "primary",
													content: "game:add",
												},
											]
										: []
								}
							/>
							<BgtCard className="p-4">
								<BgtDataTable
									columns={columns}
									data={sessions}
									noDataMessage={t("no-data-yet")}
									widths={["w-[70px]", "w-[100px]", "w-[75px]"]}
									renderSubRow={(session) =>
										game.isAsymmetric ? (
											<SessionFactionsRow session={session} players={players} gameId={game.id} />
										) : null
									}
									align="center"
									dividers
									groupRows
								/>
								<BgtDeleteModal
									open={sessionToDelete !== null}
									close={() => setSessionToDelete(null)}
									onDelete={() => sessionToDelete && deleteSession(sessionToDelete)}
									title={t("sessions:delete.title")}
									description={t("sessions:delete.description")}
								/>
							</BgtCard>
						</>
					);
				}}
			</BgtPageContent>
		</BgtPage>
	);
}
